import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Eyebrow from "@/components/site/Eyebrow";
import { ACCENT, NAVY, CREAM } from "@/lib/siteTheme";
import heronIcon from "@/assets/design/blue-heron-icon.webp";
import Turnstile, { type TurnstileHandle } from "@/components/Turnstile";

const inputStyle = { border: "1px solid rgba(14,40,70,.15)", background: "#FBFAF7", color: NAVY };

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifyError, setVerifyError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!turnstileToken) {
      setVerifyError(true);
      return;
    }
    const form = e.currentTarget;
    const formData = {
      name: (form.elements.namedItem("name") as HTMLInputElement).value,
      email: (form.elements.namedItem("email") as HTMLInputElement).value,
      phone: (form.elements.namedItem("phone") as HTMLInputElement).value,
      business: (form.elements.namedItem("business") as HTMLInputElement).value,
      service: (form.elements.namedItem("service") as HTMLSelectElement).value,
      message: (form.elements.namedItem("message") as HTMLTextAreaElement).value,
      source: "contact",
      turnstileToken,
    };
    setSubmitting(true);
    setVerifyError(false);
    try {
      const res = await fetch("/api/submit-contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.status === 403) {
        setVerifyError(true);
        turnstileRef.current?.reset();
        return;
      }
      if (!res.ok) console.error("Contact submit returned", res.status);
    } catch (err) {
      console.error("Webhook error:", err);
    } finally {
      setSubmitting(false);
    }
    setSubmitted(true);
  };

  return (
    <Layout active="contact" background={NAVY}>
      <div style={{ fontFamily: "'Archivo', sans-serif", background: NAVY }} className="bh-contact w-full antialiased">
        <Seo
          title="Contact | Blue Heron Marketing"
          description="Tell me about your business and what you're trying to achieve. Blue Heron Marketing follows up within one business day."
          path="/contact"
        />

        {/* Hero */}
        <section className="relative overflow-hidden px-6 md:px-14 pt-[120px] pb-[88px]" style={{ background: NAVY }}>
          <div className="absolute -right-[140px] -bottom-10 pointer-events-none" style={{ width: 560, maxWidth: "55vw", opacity: 0.06, transform: "scaleX(-1)" }}>
            <img src={heronIcon} alt="" width={500} height={366} className="w-full h-auto block brightness-0 invert" style={{ animation: "bhFloat 11s ease-in-out infinite" }} />
          </div>
          <div className="max-w-[1280px] mx-auto relative z-10">
            <Eyebrow dark>Contact</Eyebrow>
            <h1 className="font-black leading-[0.98] tracking-[-0.02em] mb-6" style={{ color: CREAM, fontSize: "clamp(40px,6vw,76px)" }}>
              Let's talk.
            </h1>
            <p className="max-w-[580px] leading-relaxed" style={{ color: "rgba(247,243,236,0.78)", fontSize: "clamp(17px,1.6vw,20px)" }}>
              Tell me about your business and what you're trying to achieve. I'll follow up within one business day.
            </p>
          </div>
        </section>

        {/* Form + info */}
        <section className="px-6 md:px-14 pb-[140px]" style={{ background: CREAM }}>
          <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-8 items-start relative z-[2]" style={{ marginTop: -56 }}>
            <div className="bg-white rounded-[28px] p-12" style={{ border: "1px solid rgba(14,40,70,.08)", boxShadow: "0 30px 60px rgba(14,40,70,.1)" }}>
              {submitted ? (
                <div className="py-10 text-center">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 font-black text-2xl" style={{ background: ACCENT, color: NAVY }}>✓</div>
                  <h3 className="font-extrabold text-2xl mb-2.5" style={{ color: NAVY }}>Message sent.</h3>
                  <p className="leading-relaxed" style={{ color: "rgba(17,33,47,0.6)" }}>Thanks for reaching out, I'll get back to you within one business day.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label htmlFor="name" className="block font-bold text-[13px] mb-2" style={{ color: NAVY }}>Name</label>
                      <input id="name" name="name" type="text" required placeholder="Jane Smith" className="w-full box-border px-4 py-3 rounded-xl text-[15px]" style={inputStyle} />
                    </div>
                    <div>
                      <label htmlFor="email" className="block font-bold text-[13px] mb-2" style={{ color: NAVY }}>Email</label>
                      <input id="email" name="email" type="email" required placeholder="jane@business.com" className="w-full box-border px-4 py-3 rounded-xl text-[15px]" style={inputStyle} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label htmlFor="phone" className="block font-bold text-[13px] mb-2" style={{ color: NAVY }}>Phone</label>
                      <input id="phone" name="phone" type="tel" required placeholder="(816) 555-0123" className="w-full box-border px-4 py-3 rounded-xl text-[15px]" style={inputStyle} />
                    </div>
                    <div>
                      <label htmlFor="business" className="block font-bold text-[13px] mb-2" style={{ color: NAVY }}>Business Name</label>
                      <input id="business" name="business" type="text" placeholder="Your Business" className="w-full box-border px-4 py-3 rounded-xl text-[15px]" style={inputStyle} />
                    </div>
                  </div>
                  <div className="mb-5">
                    <label htmlFor="service" className="block font-bold text-[13px] mb-2" style={{ color: NAVY }}>What do you need help with?</label>
                    <select id="service" name="service" className="w-full box-border px-4 py-3 rounded-xl text-[15px]" style={inputStyle}>
                      <option>Website</option>
                      <option>Local SEO</option>
                      <option>Google Ads</option>
                      <option>Not sure yet</option>
                    </select>
                  </div>
                  <div className="mb-7">
                    <label htmlFor="message" className="block font-bold text-[13px] mb-2" style={{ color: NAVY }}>Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      placeholder="Tell me a bit about your business and goals..."
                      className="w-full box-border px-4 py-3 rounded-xl text-[15px] resize-y"
                      style={{ ...inputStyle, fontFamily: "inherit" }}
                    />
                  </div>
                  <Turnstile
                    ref={turnstileRef}
                    onToken={(token) => {
                      setTurnstileToken(token);
                      if (token) setVerifyError(false);
                    }}
                    className="mb-5"
                  />
                  {verifyError && (
                    <p className="text-sm font-semibold mb-4" style={{ color: "#b3261e" }}>
                      Please complete the verification check above and try again.
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 font-extrabold text-base py-4 rounded-full border-none cursor-pointer transition-transform hover:-translate-y-0.5"
                    style={{ background: ACCENT, color: NAVY }}
                  >
                    {submitting ? "Sending…" : "Send Message →"}
                  </button>
                </form>
              )}
            </div>

            <div className="flex flex-col gap-5">
              <div className="rounded-[24px] px-8 py-9" style={{ background: NAVY }}>
                <div className="font-bold text-xs tracking-[0.1em] uppercase mb-[18px]" style={{ color: "rgba(247,243,236,0.5)" }}>Direct</div>
                <a href="tel:+18163704263" className="block font-extrabold text-[22px] no-underline mb-1.5" style={{ color: CREAM }}>(816) 370-4263</a>
                <div className="text-[15px] mb-6" style={{ color: "rgba(247,243,236,0.6)" }}>Kansas City Area</div>
                <Link
                  to="/book"
                  className="inline-flex items-center gap-2 font-extrabold text-sm px-[22px] py-3.5 rounded-full no-underline transition-transform hover:-translate-y-0.5"
                  style={{ background: ACCENT, color: NAVY }}
                >
                  Or Book a Call →
                </Link>
              </div>
              <div className="bg-white rounded-[24px] p-8" style={{ border: "1px solid rgba(14,40,70,.08)" }}>
                <div className="flex flex-col gap-4">
                  {["Free 30-minute consultation", "No pressure, no sales pitch", "Response within one business day"].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <span className="font-extrabold text-lg" style={{ color: ACCENT }}>✓</span>
                      <span className="font-bold text-[15px]" style={{ color: NAVY }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .bh-contact h1, .bh-contact h2, .bh-contact h3 {
          font-family: 'Archivo', sans-serif;
        }
        .bh-contact input::placeholder, .bh-contact textarea::placeholder {
          color: rgba(14,40,70,0.35);
        }
        @keyframes bhFloat {
          0% { transform: translateY(0) rotate(-6deg); }
          50% { transform: translateY(-22px) rotate(-4deg); }
          100% { transform: translateY(0) rotate(-6deg); }
        }
      ` }} />
    </Layout>
  );
};

export default Contact;
